import { type JSX,MouseEventHandler, useState } from 'react';

import { GridFeatureMode } from '@mui/x-data-grid';
import { Roles } from 'common/constants/roles';
import { usePalmButtonEvaluator } from 'common/hooks/usePalmButtonEvaluator';
import { FetchExportDataResult } from 'common/types/TypeDefs';
import { isFilterApplied } from 'common/utils/filterHelpers';

import { FilterBar } from '../filter/FilterBar';
import { FilterProps } from '../filter/FilterBar.types';

import { CreateButton } from './CreateButton';
import { ExportButton } from './ExportButton';
import { FilterButton } from './FilterButton';
import { RefreshButton } from './RefreshButton';
import { TableWrapper } from './TableWrapper';

type GridBarProps = {
  createButtonName: string;
  createButtonOnClick?: MouseEventHandler<HTMLButtonElement>;
  isDisabled: boolean;
  filter?: FilterProps;
  shouldShowCreateButton?: boolean;
  refreshButtonOnClick?: MouseEventHandler<HTMLButtonElement>;
  filterBarDisabled?: boolean;
  searchFilterDisabled?: boolean;
  shouldShowExportButton?: boolean;
  paginationMode: GridFeatureMode;
  fetchExportData: () => Promise<FetchExportDataResult>;
  title?: string;
};

export function GridBar({
    createButtonName,
    createButtonOnClick,
    isDisabled,
    filter,
    shouldShowCreateButton = true,
    refreshButtonOnClick,
    filterBarDisabled,
    searchFilterDisabled = false,
    shouldShowExportButton = false,
    paginationMode,
    fetchExportData,
    title
}: Readonly<GridBarProps>): JSX.Element {
    const [shouldShowFilter, setShouldShowFilter] = useState<boolean>(true);
    const filterIsApplied = isFilterApplied(filter?.filters);

    const handleFilterButtonClick = () => setShouldShowFilter(!shouldShowFilter);
    const handleExportButtonClick = async (): Promise<FetchExportDataResult> => {
        return await fetchExportData?.();
    };

    const hasRoleToExportData = !usePalmButtonEvaluator([ Roles.Admin, Roles.UserManager, Roles.AppManager, Roles.GlobalRoleManager ]);

    return (
        <>
            <TableWrapper>
                <FilterButton
                    onClickHandler={handleFilterButtonClick}
                    isFilterApplied={filterIsApplied}
                />
                {shouldShowExportButton && hasRoleToExportData &&
                    <ExportButton
                        onExportHandler={handleExportButtonClick}
                        title={title}
                    />
                }
                <RefreshButton disabled = {filterBarDisabled}
                    onClickHandler={refreshButtonOnClick}
                />
                {shouldShowCreateButton &&
                    <CreateButton
                        onClickHandler={createButtonOnClick}
                        disabled={isDisabled}
                        buttonName={createButtonName}
                    />
                }
            </TableWrapper>
            <FilterBar
                shouldShowFilter={shouldShowFilter}
                setShouldShowFilter={setShouldShowFilter}
                filter={filter}
                filterBarDisabled={filterBarDisabled}
                searchFilterDisabled = {searchFilterDisabled}
                paginationMode={paginationMode}
            />
        </>
    );
}
